import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { createdAtMs } from '../lib/format';
import { filterByRadius, hasAlgolia, searchListings, sortListings } from '../lib/search';

/**
 * Load approved listings. Prefer Algolia when configured for search/geo,
 * but always fall back to Firestore if Algolia fails or returns nothing.
 */
export function useListings({
  category = 'All',
  queryText = '',
  location = null,
  radiusMiles = 50,
  sort = 'newest',
  enabled = true,
} = {}) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [source, setSource] = useState('none');

  useEffect(() => {
    if (!enabled) {
      setListings([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    let cancelled = false;
    let unsubFirestore = null;
    setLoading(true);
    setError('');

    const applyFirestore = () => {
      const q = query(
        collection(db, 'listings'),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc'),
      );
      unsubFirestore = onSnapshot(
        q,
        (snap) => {
          if (cancelled) return;
          const rows = snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              createdAtMs: createdAtMs(data),
              contentUpdatedAt: data.contentUpdatedAt,
              priceDroppedAt: data.priceDroppedAt,
            };
          });
          setListings(rows);
          setSource('firestore');
          setLoading(false);
        },
        (err) => {
          if (cancelled) return;
          console.error('[Sosello] listings load failed', err);
          setError(err.message || 'Could not load listings');
          setListings([]);
          setLoading(false);
        },
      );
    };

    const run = async () => {
      const wantAlgolia =
        hasAlgolia() && Boolean(queryText.trim() || (location?.lat != null && location?.lng != null));

      if (wantAlgolia) {
        try {
          const results = await searchListings({
            query: queryText,
            category,
            lat: location?.lat,
            lng: location?.lng,
            radiusMiles,
            sort,
          });
          if (cancelled) return;
          // Algolia empty (common when geo index is thin) → Firestore fallback
          if (Array.isArray(results) && results.length > 0) {
            setListings(results);
            setSource('algolia');
            setLoading(false);
            return;
          }
        } catch (err) {
          if (!cancelled) {
            console.warn('[Sosello] Algolia search failed, using Firestore', err);
          }
        }
      }

      if (!cancelled) applyFirestore();
    };

    run();

    return () => {
      cancelled = true;
      unsubFirestore?.();
    };
  }, [category, queryText, location?.lat, location?.lng, radiusMiles, sort, enabled]);

  const filtered = useMemo(() => {
    let rows = listings;

    // Algolia already applied category/geo when source is algolia;
    // still apply client filters for Firestore results.
    if (source !== 'algolia') {
      if (category !== 'All') rows = rows.filter((x) => x.category === category);
      if (queryText.trim()) {
        const q = queryText.toLowerCase().trim();
        rows = rows.filter((x) =>
          `${x.title} ${x.description} ${x.category} ${x.pickup?.label || ''}`
            .toLowerCase()
            .includes(q),
        );
      }
      if (location?.lat != null && location?.lng != null) {
        rows = filterByRadius(rows, location.lat, location.lng, radiusMiles);
      }
    } else if (category !== 'All') {
      rows = rows.filter((x) => x.category === category);
    }

    return sortListings(rows, sort, location?.lat, location?.lng);
  }, [listings, category, queryText, location, radiusMiles, sort, source]);

  return { listings: filtered, loading, error };
}
